import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../../context/ThemeContext";

function formatPrice(value) {
  if (value == null) return "—";
  return Number(value).toLocaleString("id-ID", { maximumFractionDigits: 2 });
}

function formatIdr(value) {
  if (value == null) return "—";
  const n = Number(value);
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
}

export const LiveSpotFeed = ({ levels }) => {
  const { theme } = useTheme();
  const containerRef = useRef(null);
  const symbol = levels?.symbol;

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !symbol) return;

    el.innerHTML =
      '<div class="tradingview-widget-container__widget" style="height:100%;width:100%"></div>';

    const script = document.createElement("script");
    script.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: `IDX:${symbol}`,
      interval: "D",
      timezone: "Asia/Jakarta",
      theme: theme === "dark" ? "dark" : "light",
      style: "1",
      locale: "id",
      hide_side_toolbar: true,
      allow_symbol_change: false,
      save_image: false,
      calendar: false,
    });
    el.appendChild(script);

    return () => {
      el.innerHTML = "";
    };
  }, [symbol, theme]);

  if (!levels || !symbol) {
    return (
      <div
        className="panel lg:col-span-5 flex flex-col min-h-[360px] p-4"
        data-testid="live-spot-feed"
      >
        <span className="label-uppercase text-magenta">Live Spot Feed</span>
        <p className="mt-2 text-sm text-text-secondary font-mono">
          Pilih saham di Market Graph untuk memuat chart TradingView.
        </p>
      </div>
    );
  }

  const chips = [
    { label: "Composite", value: levels.compositeScore != null ? levels.compositeScore.toFixed(1) : "—" },
    { label: "Consensus", value: levels.consensus != null ? levels.consensus.toFixed(0) : "—" },
    { label: "Rank", value: levels.priorityRank != null ? `#${levels.priorityRank}` : "—" },
    { label: "Target", value: levels.targetSizeIdr != null ? `Rp ${formatIdr(levels.targetSizeIdr)}` : "—" },
    { label: "Bandamon", value: levels.bandamonFlow || "—" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.2 }}
      className="panel lg:col-span-5 flex flex-col min-h-[360px]"
      data-testid="live-spot-feed"
    >
      <div className="panel-header">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-sm font-bold tracking-tight uppercase">
            Live Spot Feed
          </h3>
          <span className="label-micro">
            {symbol} · IDX · D{levels.isHeld ? " · HELD" : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="label-micro">LAST</span>
          <span data-testid="spot-last-price" className="text-base font-mono num text-positive">
            Rp {formatPrice(levels.lastPrice || levels.current)}
          </span>
        </div>
      </div>

      <div className="flex-1 min-h-[320px] p-2">
        <div
          ref={containerRef}
          className="tradingview-widget-container w-full h-full"
          style={{ minHeight: 320 }}
          data-testid="tradingview-widget"
        />
      </div>

      <div className="border-t border-border px-3 py-2 flex items-center gap-2 overflow-x-auto">
        {chips.map((c) => (
          <div
            key={c.label}
            className="shrink-0 flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 border border-border"
            style={{ borderRadius: 2 }}
            data-testid={`spot-chip-${c.label.toLowerCase()}`}
          >
            <span className="text-text-secondary uppercase tracking-[0.12em]">{c.label}</span>
            <span className="text-foreground num">{c.value}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default LiveSpotFeed;
